import { MouseEvent, useMemo, useState } from "react";

import { useReactiveVar } from "@apollo/client";
import {
  CheckImported,
  CheckVerified,
  FarcasterIcon,
  Pencil as PencilIcon,
  Rodeo as RodeoIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { SHADOW_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import TwitterIcon from "@hl/shared-features/lib/assets/icons.generated/TwitterIcon";
import WalletIcon from "@hl/shared-features/lib/assets/icons.generated/WalletIcon";
import WebsiteIcon from "@hl/shared-features/lib/assets/icons.generated/WebsiteIcon";
import ProfilePlaceholderImage from "@hl/shared-features/lib/assets/images/profile-placeholder.png";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  accountSettingsVar,
  identityVar,
} from "@hl/shared-features/lib/features/auth/vars";
import { ResizedImage } from "@hl/shared-features/lib/features/image";
import { AppSEO, NotFound } from "@hl/shared-features/lib/features/layout";
import HighlightedNotes from "@hl/shared-features/lib/features/markdown/HighlightedNotes";
import { useGetPublicAccountEditorialQuery } from "@hl/shared-features/lib/features/markdown/queries.graphql.generated";
import {
  getProfileDisplayName,
  maskWallet,
} from "@hl/shared-features/lib/utils/profile";
import {
  Anchor,
  AppShell,
  Box,
  Button,
  Container,
  Group,
  Indicator,
  CopyButton as MantineCopyButton,
  Stack,
  Text,
  Title,
  Tooltip,
  createStyles,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { isAddress } from "viem";
import { useEnsAddress } from "wagmi";

import { ReactComponent as VerifiedIcon } from "../../assets/icons/verified-icon.svg";
import { USER_BASE_URL, USER_URL } from "../../config";
import { AppHeader } from "../layout/Header";

import { RequestVerificationModal } from "./RequestVerificationModal";
import UserProfileSkeleton from "./UserProfileSkeleton";
import UserTabs from "./UserTabs";
import { UserFollowers } from "./followers-and-watchlist/UserFollowers";
import { usePublicUserProfileQuery } from "./queries/profile.graphql.generated";

const useStyles = createStyles((theme) => ({
  link: {
    textDecoration: "none",
  },
  appShellMain: {
    paddingLeft: "20px !important",
    paddingRight: "20px !important",
    [theme.fn.largerThan(theme.breakpoints.md)]: {
      paddingLeft: "40px !important",
      paddingRight: "40px !important",
    },
  },
}));

export const UserProfile = () => {
  const { slug: originalSlug } = useParams();
  const slug = originalSlug?.[0] === "@" ? originalSlug.substring(1) : null;
  const { classes } = useStyles();
  const navigate = useNavigate();

  const { walletAddress } = useAuth();
  const identity = useReactiveVar(identityVar);
  const [requestVerificationModalOpened, setRequestVerificationModalOpened] =
    useState(false);

  const { data: slugEnsData, isLoading: loadingSlugEns } = useEnsAddress({
    chainId: 1,
    name: slug != null && !isAddress(slug) ? slug : undefined,
  });

  const { data: authEnsData, isLoading: loadingAuthEns } = useEnsAddress({
    chainId: 1,
    name:
      walletAddress != null && !isAddress(walletAddress)
        ? walletAddress
        : undefined,
  });

  const resolvedSlugAddress = useMemo(() => {
    if (slug == null) return null;
    if (isAddress(slug)) return slug;
    return slugEnsData;
  }, [slug, slugEnsData]);

  const resolvedWalletAddress = useMemo(() => {
    if (walletAddress == null) return null;
    if (isAddress(walletAddress)) return walletAddress;
    return authEnsData;
  }, [walletAddress, authEnsData]);

  const { data, loading } = usePublicUserProfileQuery({
    variables: {
      slug: resolvedSlugAddress!,
    },
    skip: resolvedSlugAddress == null,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { data: editorialData } = useGetPublicAccountEditorialQuery({
    variables: {
      slug: resolvedSlugAddress!,
    },
    skip: resolvedSlugAddress == null,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const accountSettings = useReactiveVar(accountSettingsVar);
  const hasWarning = [
    accountSettings?.socials.email?.address &&
      !accountSettings?.socials.email.verifiedOn,
  ].filter((value) => value).length;

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md}px)`);

  if (!slug) {
    return <NotFound />;
  }

  const profile = data?.getPublicAccountSettings;

  const isOwner = !!(
    resolvedWalletAddress &&
    profile?.walletAddresses?.some(
      (address) =>
        address.toLowerCase() === resolvedWalletAddress.toLowerCase() ||
        identity?.account?.ensName === address
    )
  );

  if ((data == null && loading) || loadingSlugEns || loadingAuthEns) {
    return <UserProfileSkeleton />;
  }

  const name = getProfileDisplayName(
    profile?.displayName,
    profile?.walletAddresses,
    slug
  );

  const isEdited =
    profile?.bio ||
    profile?.website ||
    profile?.avatarUrl ||
    profile?.displayName;

  const editProfile = () =>
    navigate(
      `${USER_URL.user.accountSettings.default.replace(":slug", slug ?? "")}`
    );

  const editorial = editorialData?.getPublicAccountSettings.editorial;

  return (
    <>
      <AppSEO title={`${name} - Profile`} description={profile?.bio || ""}>
        <link rel="canonical" href={`${USER_BASE_URL}/@${name}`} />
      </AppSEO>

      <AppShell
        header={<AppHeader />}
        classNames={{ main: classes.appShellMain }}
      >
        <Container size={1200} pb={80}>
          <Stack spacing={24} align="center">
            <Stack spacing={10} align="center">
              <ResizedImage
                src={profile?.displayAvatar || ProfilePlaceholderImage}
                fit="fill"
                radius={999}
                height={160}
                width={160}
                sx={(theme) => ({
                  border: `0.5px solid ${theme.colors.divider[0]}`,
                  borderRadius: 999,
                  boxSizing: "unset",
                })}
              />
              <Box>
                <Group spacing={8} align="center">
                  <Title order={isMobile ? 3 : 2}>{name}</Title>
                  {profile?.verified && (
                    <CheckVerified height={24} width={24} />
                  )}
                  {!profile?.verified && profile?.imported && (
                    <CheckImported height={24} width={24} />
                  )}
                </Group>
              </Box>
            </Stack>
            <Group spacing="md" position="center">
              {profile?.website && (
                <Anchor
                  underline={false}
                  href={profile.website}
                  target="_blank"
                >
                  <Group align="center" spacing={8}>
                    <WebsiteIcon />
                    <Text size="sm">
                      {profile.website.replace(/^https?:\/\//, "")}
                    </Text>
                  </Group>
                </Anchor>
              )}
              {profile?.socials?.twitter?.username && (
                <Anchor
                  underline={false}
                  href={`https://x.com/${profile.socials.twitter.username}`}
                  target="_blank"
                >
                  <Group align="center" spacing={8}>
                    <TwitterIcon width={17} height={16} />
                    <Text size="sm">
                      @{profile?.socials?.twitter?.username}
                    </Text>
                  </Group>
                </Anchor>
              )}
              {profile?.socials?.rodeo?.username && (
                <Anchor
                  underline={false}
                  href={`https://rodeo.club/@${profile?.socials?.rodeo?.username}`}
                  target="_blank"
                >
                  <Group align="center" spacing={8}>
                    <RodeoIcon width={17} height={16} />
                    <Text size="sm">@{profile?.socials?.rodeo?.username}</Text>
                  </Group>
                </Anchor>
              )}
              {profile?.socials?.farcaster?.username && (
                <Anchor
                  underline={false}
                  href={`https://warpcast.com/${profile?.socials?.farcaster?.username}`}
                  target="_blank"
                >
                  <Group align="center" spacing={8}>
                    <FarcasterIcon width={17} height={16} />
                    <Text size="sm">
                      @{profile?.socials?.farcaster?.username}
                    </Text>
                  </Group>
                </Anchor>
              )}
              {profile?.walletAddresses?.length && (
                <MantineCopyButton value={profile.walletAddresses.join(",")}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied!" : "Copy"}
                      withArrow
                      onClick={(e: MouseEvent<HTMLDivElement>) => {
                        copy();
                        e.preventDefault();
                      }}
                    >
                      <Group
                        align="center"
                        spacing={8}
                        sx={{ cursor: "pointer" }}
                      >
                        <WalletIcon />
                        <Text size="sm">
                          {profile.walletAddresses
                            ?.map((wallet) => maskWallet(wallet))
                            .join(",")}
                        </Text>
                      </Group>
                    </Tooltip>
                  )}
                </MantineCopyButton>
              )}
            </Group>
            {profile?.bio ? (
              <Text
                size="sm"
                sx={{ whiteSpace: "pre-line", overflow: "hidden" }}
                align="center"
                maw={"640px"}
              >
                {profile?.bio}
              </Text>
            ) : (
              isOwner && (
                <Text color={TEXT_COLOR.SECONDARY}>
                  You haven’t added a bio yet. Edit your profile to add one.
                </Text>
              )
            )}
            {editorial && <HighlightedNotes editorial={editorial} />}
            {isOwner && (
              <Stack spacing={32}>
                <Group spacing={20} position="center">
                  <Indicator
                    zIndex={99}
                    label={
                      hasWarning ? (
                        <Text
                          size="sm"
                          lh={1}
                          color={TEXT_COLOR.INVERTED_PRIMARY}
                          weight={WEIGHT_BOLD}
                        >
                          !
                        </Text>
                      ) : null
                    }
                    dot={false}
                    color="warningStatus.0"
                    size={16}
                  >
                    <Button
                      color={SHADOW_COLOR}
                      size="lg"
                      leftIcon={<PencilIcon />}
                      onClick={() => editProfile()}
                    >
                      {isEdited ? "Edit your profile" : "Set up your profile"}
                    </Button>
                  </Indicator>
                  {!profile?.verified && (
                    <Button
                      color={SHADOW_COLOR}
                      size="lg"
                      leftIcon={<VerifiedIcon />}
                      onClick={() => setRequestVerificationModalOpened(true)}
                    >
                      Request verification
                    </Button>
                  )}
                </Group>
              </Stack>
            )}
            <UserFollowers walletAddress={resolvedSlugAddress} />
          </Stack>
        </Container>
        {resolvedSlugAddress != null && (
          <UserTabs
            slug={slug}
            slugAddress={resolvedSlugAddress}
            isOwner={isOwner}
          />
        )}
      </AppShell>
      <RequestVerificationModal
        isOpened={requestVerificationModalOpened}
        onClose={() => setRequestVerificationModalOpened(false)}
      />
    </>
  );
};
